// filepath: apps/web/src/admin/organization-units/detail.tsx
//
// ISS-517 — OrganizationUnitDetailScreen.
//
// Read-only detail view of the flat Go wire (FR-DIR-2,
// internal/directory/organization_unit/package.go:40-52): id, tenant,
// legal_entity_id, parent_organization_unit_id, name, code, status,
// closed_at, row_version, created_at, created_by_subject.
//
// INV-2: nothing server-joined is rendered. The unit carries ONLY
// `legal_entity_id`; the legal-entity NAME (if the operator needs it)
// is joined at render time from `GET /api/directory/legal-entities`,
// whose payload is itself server-emitted — never synthesised.
//
// INV-7: Archive carries `row_version`. The Go close handler
// (`handlers.go:190-215`) rejects a stale submission with 409 +
// code='row_version_mismatch' (`handlers.go:329-330`) and the SPA
// surfaces a <StaleVersionError> with a Reload action rather than
// silently overwriting.
//
// Archive semantics: `Close` sets status='closed' + closed_at=now()
// (`repository.go:206-222`). It does NOT cascade-close children and
// this screen does not hide them — the server does not enforce a
// cascade, so hiding them client-side would misrepresent the data.
//
// INV-9: no dangerouslySetInnerHTML; every value flows through JSX
// text / design-system primitives.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDatePattern } from "@/i18n/format";
import { Card } from "@/design-system/Card";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { Dialog } from "@/design-system/Dialog";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  archiveOrganizationUnit,
  getOrganizationUnit,
  newIdempotencyKey,
} from "./api";
import type { OrganizationUnit } from "@/features/directory/types";

function handleSpaNavigate(
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
): void {
  if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return;
  }
  e.preventDefault();
  navigate(href);
}

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

function isRowVersionConflict(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    e.status === 409 &&
    e.code === "row_version_mismatch"
  );
}

export interface OrganizationUnitDetailScreenProps {
  id?: string;
  loader?: (id: string) => Promise<OrganizationUnit>;
  archiver?: (
    id: string,
    expectedRowVersion: number,
    key: string,
  ) => Promise<void>;
}

export function OrganizationUnitDetailScreen({
  id = "",
  loader,
  archiver,
}: OrganizationUnitDetailScreenProps = {}): React.ReactElement {
  const { t, locale } = useTranslation();
  const [row, setRow] = useState<OrganizationUnit | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [archiving, setArchiving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [stale, setStale] = useState<boolean>(false);
  const [archived, setArchived] = useState<boolean>(false);
  // BA spec: "archive with confirmation". The dialog gates the POST
  // /{id}/close call — a single click never closes a unit.
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    setStale(false);
    setArchived(false);
    try {
      const f = loader ?? ((pid: string) => getOrganizationUnit(pid));
      setRow(await f(id));
    } catch (e) {
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  useEffect(() => {
    void load();
  }, [load]);

  const onArchive = useCallback(async () => {
    if (!row) return;
    setArchiving(true);
    setError(null);
    setStale(false);
    try {
      const key = newIdempotencyKey();
      if (archiver) {
        await archiver(row.id, row.row_version, key);
      } else {
        await archiveOrganizationUnit(
          row.id,
          { row_version: row.row_version },
          key,
        );
      }
      setConfirmOpen(false);
      setArchived(true);
      await load();
    } catch (e) {
      if (isRowVersionConflict(e)) {
        setStale(true);
      } else if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setArchiving(false);
    }
  }, [row, archiver, load]);

  if (loading) {
    return (
      <p data-testid="organization-unit-detail-loading">
        {t(
          "directory.organization_units.detail.loading",
          "Loading organization unit…",
        )}
      </p>
    );
  }
  if (permissionDenied) {
    return (
      <div data-testid="organization-unit-detail-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="organization-unit-detail-error">
        {t("directory.organization_units.detail.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="organization-unit-detail-not-found">
        {t(
          "directory.organization_units.detail.not_found",
          "Organization unit not found",
        )}
      </p>
    );
  }

  return (
    <section data-testid="organization-unit-detail-screen">
      <header>
        <h1 id="organization-unit-detail-title">{row.name}</h1>
        <div className="organization-unit-detail-actions">
          <a
            href={`/admin/organization-units/${encodeURIComponent(row.id)}/edit`}
            data-testid="organization-unit-detail-edit-link"
            onClick={(e) =>
              handleSpaNavigate(
                e,
                `/admin/organization-units/${encodeURIComponent(row.id)}/edit`,
              )
            }
            style={{ textDecoration: "none" }} // design-adherence-ignore: link reset for a button-styled anchor; no underline, no colour token.
          >
            <Button variant="primary">
              {t("directory.organization_units.detail.edit", "Edit")}
            </Button>
          </a>
          <a
            href="/admin/organization-units"
            data-testid="organization-unit-detail-back-link"
            onClick={(e) => handleSpaNavigate(e, "/admin/organization-units")}
            style={{ textDecoration: "none" }} // design-adherence-ignore: link reset (see above).
          >
            <Button variant="secondary">
              {t("directory.organization_units.detail.back", "Back")}
            </Button>
          </a>
        </div>
      </header>

      {archived ? (
        // design-adherence-ignore: <Banner> test hook via wrapper.
        <span data-testid="organization-unit-detail-archived">
          <Banner tone="info">
            {t(
              "directory.organization_units.detail.archived",
              "Organization unit archived.",
            )}
          </Banner>
        </span>
      ) : null}
      {stale ? (
        <StaleVersionError
          entityName={row.name}
          onReload={() => void load()}
          data-testid="organization-unit-detail-stale"
        />
      ) : null}

      <Card>
        <dl>
          <dt>{t("directory.organization_units.detail.name", "Name")}</dt>
          <dd>{row.name}</dd>
          <dt>{t("directory.organization_units.detail.code", "Code")}</dt>
          <dd data-testid="organization-unit-detail-code">{row.code ?? "—"}</dd>
          <dt>
            {t(
              "directory.organization_units.detail.legal_entity",
              "Legal entity",
            )}
          </dt>
          {/* INV-2: the wire carries legal_entity_id only. No name is
              synthesised client-side. */}
          <dd data-testid="organization-unit-detail-legal-entity-id">
            {row.legal_entity_id}
          </dd>
          <dt>
            {t(
              "directory.organization_units.detail.parent",
              "Parent organization unit",
            )}
          </dt>
          <dd data-testid="organization-unit-detail-parent-id">
            {row.parent_organization_unit_id ?? "—"}
          </dd>
          <dt>{t("directory.organization_units.detail.status", "Status")}</dt>
          <dd>
            <Badge tone={row.status === "active" ? "success" : "neutral"}>
              {t(
                `directory.organization_units.status.${row.status}`,
                row.status,
              )}
            </Badge>
          </dd>
          <dt>
            {t("directory.organization_units.detail.closed_at", "Closed at")}
          </dt>
          <dd data-testid="organization-unit-detail-closed-at">
            {row.closed_at
              ? formatDatePattern(new Date(row.closed_at), locale)
              : "—"}
          </dd>
          <dt>
            {t(
              "directory.organization_units.detail.row_version",
              "Row version",
            )}
          </dt>
          <dd>{row.row_version}</dd>
          <dt>
            {t("directory.organization_units.detail.created_at", "Created at")}
          </dt>
          <dd>{formatDatePattern(new Date(row.created_at), locale)}</dd>
          <dt>
            {t("directory.organization_units.detail.created_by", "Created by")}
          </dt>
          <dd>{row.created_by_subject}</dd>
        </dl>

        {row.status !== "closed" ? (
          <div className="organization-unit-detail-archive-section">
            {/* design-adherence-ignore: <Button> test hook via wrapper. */}
            <span data-testid="organization-unit-detail-archive-button">
              <Button
                type="button"
                variant="danger"
                onClick={() => setConfirmOpen(true)}
                disabled={archiving}
              >
                {archiving
                  ? t(
                      "directory.organization_units.detail.archiving",
                      "Archiving…",
                    )
                  : t("directory.organization_units.detail.archive", "Archive")}
              </Button>
            </span>
          </div>
        ) : null}
      </Card>

      {/* Archive confirmation (BA spec: "archive with confirmation"). */}
      <span data-testid="organization-unit-detail-archive-confirm">
        <Dialog
          open={confirmOpen}
          title={t(
            "directory.organization_units.detail.archive_confirm_title",
            "Archive organization unit",
          )}
          onClose={() => setConfirmOpen(false)}
          actions={
            <>
              {/* design-adherence-ignore: <Button> test hook via wrapper. */}
              <span data-testid="organization-unit-detail-archive-cancel">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setConfirmOpen(false)}
                >
                  {t(
                    "directory.organization_units.detail.archive_cancel",
                    "Cancel",
                  )}
                </Button>
              </span>
              <span data-testid="organization-unit-detail-archive-confirm-button">
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => void onArchive()}
                  disabled={archiving}
                >
                  {archiving
                    ? t(
                        "directory.organization_units.detail.archiving",
                        "Archiving…",
                      )
                    : t(
                        "directory.organization_units.detail.archive_confirm",
                        "Archive",
                      )}
                </Button>
              </span>
            </>
          }
        >
          {t(
            "directory.organization_units.detail.archive_confirm_body",
            "Archiving closes this unit. Existing child units are not closed.",
          )}
        </Dialog>
      </span>
    </section>
  );
}

export default OrganizationUnitDetailScreen;
